import React, { useCallback, useEffect } from "react";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import SidebarItem from "./SidebarItem";
import { ReportForUI } from "@/types/report";
import { ItemDropdownMenu } from "./ItemDropdownMenu";
import { useItemManagement } from "@/hooks/useItemManagement";
import { ItemActions } from "./ItemActions";
import {
  useAuthenticationContext,
  useCurrentAccountId,
} from "@/store/authContext/authContext";
import EventBus from "@/lib/eventBus";
import { Button } from "@/components/ui/button";
import { GroupedItemsList } from "./GroupedItemsList";
import { LineChart } from "lucide-react";
import MiniSidebarSection from "./MiniSidebarSection";
import mixpanel from "@/lib/mixpanel";

interface SidebarAdvancedReportSectionProps {
  isExpanded: boolean;
  onToggle: (value: boolean) => void;
  onModalStateChange: (isOpen: boolean) => void;
  isActive: boolean;
  isMiniMode: boolean;
}

const SidebarAdvancedReportSection: React.FC<
  SidebarAdvancedReportSectionProps
> = ({ isExpanded, onToggle, onModalStateChange, isActive, isMiniMode }) => {
  const location = useLocation(),
    navigate = useNavigate();
  const matchCurrentReport = useMatch("/areport/:id");
  const matchNewReport = useMatch("/new-areport");
  const currentAccountId = useCurrentAccountId();
  const { state } = useAuthenticationContext();

  const reportsState = state.reportState.sidebarReports;
  const reports = reportsState.data;

  const refreshReports = useCallback(async () => {
    EventBus.$emit("sidebar:refreshReports");
  }, []);

  useEffect(() => {
    refreshReports();
  }, [reportsState.params.query, currentAccountId, refreshReports]);

  const {
    selectedItem: selectedReport,
    setSelectedItem: setSelectedReport,
    isRenameOpen,
    setIsRenameOpen,
    isDeleteOpen,
    setIsDeleteOpen,
    handleRename,
    handleDelete,
  } = useItemManagement("reports", refreshReports, "/new-areport", "report");

  useEffect(() => {
    setIsRenameOpen(false);
    setSelectedReport(null);
  }, [location, setIsRenameOpen, setSelectedReport]);

  const handleNewReportClick = () => {
    if (matchNewReport) {
      // if we are already on the new report page, reset the report state
      EventBus.$emit("report:resetReport");
    }
    mixpanel.track("Click", {
      eventDescription: "Report Builder+ > New Report",
    });
    navigate("/new-areport");
  };

  // Update parent modal state when either popover or dialogs are open
  useEffect(() => {
    onModalStateChange(isRenameOpen || isDeleteOpen);
  }, [isRenameOpen, isDeleteOpen, onModalStateChange]);

  const renderReportItem = (report: ReportForUI, isActive?: boolean) => (
    <div key={report.id} className="flex items-center group">
      <Link
        to={`/areport/${report.id}`}
        className={`flex-1 min-w-0 ${isActive ? "text-accent-foreground" : ""}`}
      >
        <Button
          variant="ghost"
          className={`w-full justify-start px-2 py-1.5 h-auto text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
            isActive
              ? "font-bold text-foreground dark:text-foreground bg-sky-50 dark:bg-sky-900/20"
              : ""
          }`}
          title={report.title}
          onClick={() =>
            mixpanel.track("Click", {
              eventDescription: `Report Builder+ > ${report.title}`,
            })
          }
        >
          <div className="truncate">{report.title}</div>
        </Button>
      </Link>
      <ItemDropdownMenu
        config={{
          isDuplicateEnabled: false,
          isDeleteEnabled: true,
          isRenameEnabled: true,
        }}
        onRename={() => {
          setSelectedReport(report);
          setIsRenameOpen(true);
        }}
        onDelete={() => {
          setSelectedReport(report);
          setIsDeleteOpen(true);
        }}
      />
    </div>
  );
  const reportContent = (
    <>
      <div className="block mb-1 px-6 block mb-1">
        <Button
          id="new-report-button"
          onClick={handleNewReportClick}
          variant="ghost"
          className={`my-4 px-6 py-2 w-full h-auto rounded-full border-primary border hover:bg-secondary/5 flex justify-center items-center transition-colors duration-200`}
        >
          <span className="font-bold text-[13px] bg-gradient-to-r from-gradient-color-main-end to-gradient-color-main-start text-transparent bg-clip-text">
            NEW REPORT
          </span>
        </Button>
      </div>

      <GroupedItemsList
        items={reports}
        renderItem={renderReportItem}
        viewAllLink="/reports"
        viewAllText="View All Reports"
        activeItemId={matchCurrentReport?.params.id}
        viewAllTrackingName="Report Builder+ > View All Reports"
      />
    </>
  );

  const itemActions = (
    <ItemActions
      isRenameOpen={isRenameOpen}
      isDeleteOpen={isDeleteOpen}
      itemType="Report"
      onRenameClose={() => setIsRenameOpen(false)}
      onDeleteClose={() => setIsDeleteOpen(false)}
      onRename={handleRename}
      onDelete={handleDelete}
      initialTitle={selectedReport?.title}
      onModalStateChange={onModalStateChange}
    />
  );

  if (isMiniMode) {
    return (
      <>
        <MiniSidebarSection
          icon={<LineChart size={20} />}
          label="Advanced Report Builder"
          isActive={isActive}
        >
          {reportContent}
        </MiniSidebarSection>
        {itemActions}
      </>
    );
  }

  return (
    <>
      <SidebarItem
        icon={
          <LineChart
            size={20}
            color={
              isActive
                ? "var(--sidebar-active-color)"
                : "var(--sidebar-item-color)"
            }
          />
        }
        label="Report Builder+"
        isExpanded={isExpanded}
        onToggle={onToggle}
        isActive={isActive}
      >
        {reportContent}
      </SidebarItem>
      {itemActions}
    </>
  );
};

export default SidebarAdvancedReportSection;
